import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, avatarUrl } from '../api.js';

// Module-level cache of user lookups, keyed by `${guildId}:${userId}`.
// Pending lookups are stored as Promises so concurrent UserChips don't
// trigger duplicate fetches.
const cache = new Map();

function get(guildId, userId) {
  const key = `${guildId}:${userId}`;
  if (cache.has(key)) return cache.get(key);
  const promise = api.userInfo(guildId, userId)
    .then(u => { cache.set(key, u); return u; })
    .catch(() => { cache.set(key, null); return null; });
  cache.set(key, promise);
  return promise;
}

export function UserChip({ guildId, userId, link = true, showAvatar = true, size = 22 }) {
  const [user, setUser] = useState(() => {
    const cached = cache.get(`${guildId}:${userId}`);
    return cached && !(cached instanceof Promise) ? cached : null;
  });

  useEffect(() => {
    let alive = true;
    Promise.resolve(get(guildId, userId)).then(u => { if (alive) setUser(u); });
    return () => { alive = false; };
  }, [guildId, userId]);

  const displayName = user?.nick || user?.global_name || user?.username || userId;
  const sub = user ? (user.global_name && user.username && user.global_name !== user.username ? user.username : null) : null;

  const inner = (
    <span className="user-chip">
      {showAvatar && (
        <img
          className="avatar"
          src={user ? avatarUrl(user) : avatarUrl({ id: userId })}
          alt=""
          style={{ width: size, height: size }}
        />
      )}
      <span className="user-chip-name">{displayName}</span>
      {sub && <span className="user-chip-sub">·&nbsp;{sub}</span>}
    </span>
  );

  if (!link) return inner;
  return (
    <Link to={`/g/${guildId}/users/${userId}`} className="user-chip-link" title={`User ID: ${userId}`}>
      {inner}
    </Link>
  );
}
